/**
 * Legacy format-validator API. Re-exports the canonical validators from
 * @kss/ui under the panel's older names so existing forms (Customer,
 * Supplier, Tenant, Bank, Employee, …) keep working while picking up the
 * stricter shared rules — including Verhoeff Aadhaar checksum and the
 * extended Indian regulatory set (IFSC, PIN code, TAN, CIN, UPI, HSN).
 */
import {
  isPhone, isEmail, isPan, isGstin, isAadhaar, isIfsc, isPincode, isTan, isCin, isUpi, isHsn,
} from '@kss/ui';

const passWhenEmpty = (value: string | null | undefined, fn: (v: unknown) => string | null) => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  return fn(value);
};

export function validateMobile(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isPhone);
}

export function validateEmail(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isEmail);
}

export function validatePan(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isPan);
}

export function validateGst(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isGstin);
}

export function validateAadhaar(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isAadhaar);
}

export function validateIfsc(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isIfsc);
}

export function validatePincode(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isPincode);
}

export function validateTan(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isTan);
}

export function validateCin(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isCin);
}

export function validateUpi(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isUpi);
}

export function validateHsn(value: string | null | undefined): string | null {
  return passWhenEmpty(value, isHsn);
}

export type FieldValidator = (value: string | null | undefined) => string | null;

/**
 * Build an `onBlur` handler that clears the input + shows an alert
 * when the value fails the validator. Pairs with the user's stated
 * UX: "if not valid entry then blank text box and show alert".
 *
 *   onBlur={makeBlurValidator(validateMobile, 'Mobile', toast.error,
 *           (v) => set('phone', v))}
 */
export function makeBlurValidator(
  validate: FieldValidator,
  fieldLabel: string,
  showError: (title: string, body?: string) => void,
  clearValue: (next: string) => void,
): (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
  return (e) => {
    const v = e.currentTarget.value;
    const err = validate(v);
    if (err) {
      showError(`Invalid ${fieldLabel}`, err);
      clearValue('');
    }
  };
}
